import React, {useEffect, useState} from 'react';
import {Controller, UseFormMethods} from 'react-hook-form';
import {
  IClaimsCPTData,
  IClaimsTicketDetails,
  IModuleTicketFields
} from '@shared/services/service-mappers/WorkflowMapper';
import {FormField} from '@components/system/FormField/FormField';
import {useTranslation} from 'react-i18next';
import DropDownFieldValue from '@components/system/DropDownFieldValue/DropDownFieldValue';
import './ClaimsTicketDetailsFields.scss';
import HelperUtils from '@shared/utils/HelperUtils';
import moment from 'moment';
import ClaimsCPTFields from './ClaimsCPTFields/ClaimsCPTFields';
import {FormHelperText} from '@material-ui/core';
import useIsClaimsCPTCodesEnabled from 'src/hooks/useIsClaimsCPTCodesEnabled';

interface IClaimsTicketsDetailsFields {
  isEditing: boolean;
  methods: UseFormMethods;
  clinicGroupId: number;
  ticketDetails: IClaimsTicketDetails | null;
  refreshTicketDetails: () => void;
  customFields: IModuleTicketFields[];
  clinicPayersList: any[];
  isLoadingTicketDetails: boolean;
  clinicId: number;
}

const ClaimsTicketsDetailsFields = (
  props: IClaimsTicketsDetailsFields
): JSX.Element => {
  const {
    methods,
    isEditing,
    ticketDetails,
    customFields,
    clinicPayersList,
    isLoadingTicketDetails,
    clinicId
  } = props;

  const {t} = useTranslation();

  const payerOptionsList = clinicPayersList.map(payer => {
    return {
      label: payer.payer_name,
      value: payer.id
    };
  });

  const [showCPTCodesGrid, setShowCPTCodesGrid] = useState(false);

  const ticketInputFields = customFields.map(field => {
    const inputField: Record<string, any> = {
      label: field.field_title,
      formKey: field.field_name_id,
      type: field.field_type,
      placeholder: field.field_placeholder,
      required: field.field_required
    };
    // TODO: HANDLE IT IN THE BACKEND
    if (inputField.formKey === 'payer_id') {
      inputField.itemList = payerOptionsList;
    }

    if (inputField.type === 'select' && !inputField.required) {
      inputField.onClear = () => {
        methods.setValue(inputField.formKey, null);
      };
    }

    return inputField;
  });
  const {
    patient: selectedPatient,
    claim_number: enteredClaimNumber,
    cpt_data: cptCodes
  } = methods.watch(['patient', 'claim_number', 'cpt_data']);

  const handleCloseCPTCodesGrid = () => {
    setShowCPTCodesGrid(false);
  };

  // When patient is selected, update the title to include the patient name.
  useEffect(() => {
    if (!isEditing) {
      // Claim# BCBS1234 for John Doe
      let formattedPatientName = t(
        'CLAIMS_TICKETS.TICKET_DETAILS.DEFAULT_PATIENT_NAME'
      );
      if (selectedPatient) {
        formattedPatientName = HelperUtils.formFullName(
          selectedPatient.first_name,
          selectedPatient.last_name,
          selectedPatient.middle_name
        );
      }
      methods.setValue(
        'title',
        t('CLAIMS_TICKETS.TICKET_DETAILS.DEFAULT_TICKET_TITLE_VALUE', {
          patientName: formattedPatientName,
          claimNumber: enteredClaimNumber ?? ''
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, enteredClaimNumber, isEditing]);

  useEffect(() => {
    if (isEditing && ticketDetails) {
      methods.setValue('cpt_data', ticketDetails?.claims_data?.cpt_data ?? []);
    } else {
      methods.setValue('cpt_data', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, ticketDetails]);

  const cptCurrencyFieldKeys = [
    'allowed_amount',
    'balance_amount',
    'charges',
    'insurance_amount',
    'paid_insurance_amount',
    'paid_patient_amount',
    'patient_amount'
  ];
  const isClaimsCPTCodesEnabled = useIsClaimsCPTCodesEnabled({
    clinicId
  });

  const renderClaimsFields = (
    formFieldValue: Partial<IClaimsTicketDetails['claims_data']>,
    inputFields = ticketInputFields
  ): JSX.Element => {
    return (
      <>
        {inputFields.map(({formKey, ...inputItem}) => {
          let isDisabled = false;
          if (
            cptCurrencyFieldKeys.includes(formKey) &&
            isClaimsCPTCodesEnabled
          ) {
            isDisabled = true;
          }
          return (
            <Controller
              key={formKey}
              control={methods.control}
              name={formKey}
              defaultValue={
                formFieldValue[
                  formKey as keyof IClaimsTicketDetails['claims_data']
                ] ?? null
              }
              rules={{
                validate: value => {
                  if (inputItem.required) {
                    if (!value) {
                      return t(
                        'TICKETING_SYSTEM.TICKET_DETAILS.REQUIRED_VALIDATION_ERROR',
                        {
                          fieldLabel: inputItem.label
                        }
                      ) as string;
                      // CHECK WETHER THE DATE IS VALID OR NOT
                    } else if (
                      inputItem.type === 'date' &&
                      !moment(value)?.isValid()
                    ) {
                      return t('VALIDATORS.INVALID_DATE') as string;
                    }
                    return true;
                  }
                  return true;
                }
              }}
              render={inputProps => {
                return (
                  <FormField
                    {...inputItem}
                    {...inputProps}
                    error={methods.errors[formKey]}
                    disabled={isDisabled}
                  />
                );
              }}
            />
          );
        })}
      </>
    );
  };

  function renderCreateClaimsFields(
    formFieldValue: Partial<IClaimsTicketDetails['claims_data']>
  ): JSX.Element {
    return <>{renderClaimsFields(formFieldValue)}</>;
  }

  const renderFieldsInCPTCode = (
    formFieldValue: Partial<IClaimsTicketDetails['claims_data']>
  ) => {
    const fieldsKeyToShow = ['claim_number', 'date_of_service', 'payer_id'];
    const fieldsExceptCPTInfo = ticketInputFields.filter(field =>
      fieldsKeyToShow.includes(field?.formKey)
    );
    return renderClaimsFields(formFieldValue, fieldsExceptCPTInfo);
  };

  useEffect(() => {
    const currentFieldsValue = methods.getValues();
    Object.entries(currentFieldsValue).forEach(([key, value]) => {
      methods.setValue(key, value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCPTCodesGrid]);

  function renderEditClaimsFields(
    formFieldValue: Partial<IClaimsTicketDetails['claims_data']>
  ) {
    return (
      <>
        {renderClaimsFields(formFieldValue)}
        {/* claim_data_id has to be passed to the update api, so adding it.*/}
        <Controller
          control={methods.control}
          name="claim_data_id"
          defaultValue={ticketDetails?.claims_data?.id}
          render={() => <input type="hidden" />}
        />
      </>
    );
  }

  const renderClaimsCPTCodesField = () => {
    const cptCodesList = (cptCodes ?? [])
      .map((cptItem: Partial<IClaimsCPTData>) => cptItem.cpt_code)
      .filter((i: string | undefined) => !!i)
      .join(', ');
    return (
      <>
        <DropDownFieldValue
          key={'cpt-codes'}
          label={
            <div className="cpt-codes-header">
              <p>{t('CLAIMS_TICKETS.CPT_CODES.CPT_CODE_LABEL')}</p>
              <p
                className="link-style"
                onClick={() => setShowCPTCodesGrid(true)}>
                {!!cptCodesList && cptCodesList.length > 0
                  ? t('CLAIMS_TICKETS.CPT_CODES.SHOW_CPT_CODES_LABEL')
                  : t('CLAIMS_TICKETS.CPT_CODES.ADD_CPT_CODES_LABEL')}
              </p>
            </div>
          }
          value={
            <div>
              <p className="cpt-codes-list">
                {cptCodesList ||
                  t('CLAIMS_TICKETS.CPT_CODES.NO_CPT_CODES_ADDED_LABEL')}
              </p>
              {methods.errors['cpt_data']?.message && (
                <FormHelperText error>
                  {methods.errors['cpt_data'].message}
                </FormHelperText>
              )}
            </div>
          }
        />
        <Controller
          control={methods.control}
          name="cpt_data"
          defaultValue={methods.getValues('cpt_data') ?? []}
          render={() => <input type="hidden" />}
          rules={{
            validate: value => {
              if (!value || !Array.isArray(value)) {
                return t(
                  'CLAIMS_TICKETS.CPT_CODES.ONE_CPT_LINE_IS_REQUIRED_ERROR_LABEL'
                ) as string;
              }

              const rowsWithCPTData = value.filter(itemParam => {
                if (itemParam?.deleted) {
                  return false;
                }
                return Object.values(itemParam).some(item => item);
              });

              if (rowsWithCPTData.length == 0) {
                return t(
                  'CLAIMS_TICKETS.CPT_CODES.ONE_CPT_LINE_IS_REQUIRED_ERROR_LABEL'
                ) as string;
              }

              for (const cptItem of rowsWithCPTData) {
                if (!cptItem['cpt_code']) {
                  return t(
                    'CLAIMS_TICKETS.CPT_CODES.CPT_CODE_IS_REQUIRED_ERROR_LABEL'
                  ) as string;
                }
              }
              return true;
            }
          }}
        />
        {showCPTCodesGrid && (
          <ClaimsCPTFields
            key={isLoadingTicketDetails.toString()}
            methods={methods}
            onClose={handleCloseCPTCodesGrid}
            resetCPTCodesFieldValue={
              isEditing ? ticketDetails?.claims_data?.cpt_data ?? [] : []
            }
            renderClaimsFields={renderFieldsInCPTCode}
          />
        )}
      </>
    );
  };

  return (
    <div
      className={`claims-ticket-details-fields ${
        isEditing ? 'editing-ticket-details' : ''
      }`}>
      {isEditing
        ? renderEditClaimsFields(ticketDetails?.claims_data ?? {})
        : renderCreateClaimsFields({})}
      {isClaimsCPTCodesEnabled && renderClaimsCPTCodesField()}
    </div>
  );
};

export default ClaimsTicketsDetailsFields;
